import type { LoginRequest } from '@contracts/auth'
import { isApiError } from '@/shared/api'
import type { AuthApi } from './auth.api'
import { InvalidCredentialsError, type AuthProvider } from './auth-provider'
import { toSession, type Session } from './session-model'
import type { TokenStore } from './token-store'

/** AuthProvider over the backend's token contract (contracts/auth.ts, ADR-0009). */
export class HttpAuthProvider implements AuthProvider {
  private readonly listeners = new Set<() => void>()

  constructor(
    private readonly api: AuthApi,
    private readonly tokens: TokenStore,
  ) {}

  async getSession(): Promise<Session | null> {
    // Never signed in on this browser: no need to ask the backend.
    if (this.tokens.getRefreshToken() === null) return null
    try {
      return toSession(await this.api.me())
    } catch (error) {
      if (isApiError(error) && error.status === 401) return null
      throw error
    }
  }

  async login(credentials: LoginRequest): Promise<Session> {
    try {
      const response = await this.api.login(credentials)
      this.tokens.set(response)
      return toSession(response.user)
    } catch (error) {
      if (isApiError(error) && error.status === 401)
        throw new InvalidCredentialsError(error.message)
      throw error
    }
  }

  async logout(): Promise<void> {
    const refreshToken = this.tokens.getRefreshToken()
    // Signed out locally whatever the backend answers.
    this.tokens.clear()
    if (refreshToken === null) return
    await this.api.logout({ refresh_token: refreshToken }).catch(() => undefined)
  }

  onExpired(listener: () => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  /** Called by the HttpClient when the session can no longer be renewed. */
  notifyExpired(): void {
    for (const listener of [...this.listeners]) listener()
  }
}
